import { useState, FormEvent } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, Link, Stack, TextField, Typography } from '@mui/material';
import { useAuth } from '../../shared/hooks/useAuth';
import { useApiError } from '../../shared/errors/useApiError';
import { useNotification } from '../../shared/notifications/NotificationProvider';
import { SplitAuthLayout, MarketingBenefits } from '../../shared/components/layout/SplitAuthLayout';
import { PasswordField } from '../../shared/components/ui/PasswordField';
// Google OAuth temporarily disabled until OAUTH_GOOGLE_* env vars are
// wired up on test + prod (Jenkins secret files). Re-enable this import
// and the <SocialAuthButtons /> below once the backend is configured.
// import { SocialAuthButtons } from '../../shared/components/ui/SocialAuthButtons';

export function ProLoginPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { notify } = useNotification();
  const { translate } = useApiError();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname ?? '/dashboard';

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      notify.success(t('auth.loginSuccess'));
      navigate(from, { replace: true });
    } catch (err) {
      notify.error(translate(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <SplitAuthLayout
      variant="pro"
      marketing={
        <Box>
          <Typography
            variant="overline"
            sx={{
              color: 'primary.main',
              letterSpacing: '0.18em',
              fontWeight: 700,
              display: 'block',
              mb: 1,
            }}
          >
            {t('pro.registerEyebrow')}
          </Typography>
          <Typography
            variant="h3"
            sx={{ fontWeight: 800, lineHeight: 1.15, mb: 2, color: 'inherit' }}
          >
            {t('pro.heroTitle')}
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ color: 'inherit', opacity: 0.85, fontSize: '1.0625rem' }}
          >
            {t('pro.heroSubtitle')}
          </Typography>
          <MarketingBenefits items={[t('pro.benefit1'), t('pro.benefit2'), t('pro.benefit3')]} />
        </Box>
      }
    >
      <Typography variant="h3" sx={{ mb: 1 }}>
        {t('auth.welcomeBack')}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 4 }}>
        {t('pro.loginSubtitle')}
      </Typography>

      {/* Google OAuth disabled until backend env vars are set (see import above). */}
      {/* <SocialAuthButtons variant="pro" /> */}

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Stack spacing={2.5}>
          <TextField
            label={t('auth.email')}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            fullWidth
            autoComplete="email"
          />
          <PasswordField
            label={t('auth.password')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            fullWidth
            autoComplete="current-password"
          />
          <Button
            type="submit"
            size="large"
            disabled={loading}
            fullWidth
            sx={{
              bgcolor: '#1A1816',
              color: 'common.white',
              fontWeight: 700,
              py: 1.5,
              fontSize: '1rem',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#000', boxShadow: 'none' },
              '&.Mui-disabled': {
                bgcolor: 'action.disabledBackground',
                color: 'action.disabled',
              },
            }}
          >
            {loading ? t('common.loading') : t('auth.login')}
          </Button>
        </Stack>
      </Box>

      <Stack spacing={1.25} sx={{ mt: 3, textAlign: 'center' }}>
        <Link component={RouterLink} to="/forgot-password" sx={{ color: 'primary.main' }}>
          {t('auth.forgotPassword')}
        </Link>
        <Typography variant="body2" color="text.secondary">
          {t('auth.noAccount')}{' '}
          <Link component={RouterLink} to="/pro" sx={{ color: 'primary.main', fontWeight: 600 }}>
            {t('nav.createProAccount')}
          </Link>
        </Typography>
      </Stack>

      <Box
        sx={{
          mt: 4,
          p: 2,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {t('pro.notAProShort')}{' '}
          <Link component={RouterLink} to="/login" sx={{ color: 'primary.main', fontWeight: 600 }}>
            {t('auth.customerLoginLink')}
          </Link>
        </Typography>
      </Box>
    </SplitAuthLayout>
  );
}
